import { Controller, useFormContext, useWatch } from "react-hook-form"
import { ProfilAssetType, useDropdownDepartment, useDropdownDivisi } from "../../utils"
import { Button, DateInput, Input, Label, NumberInput, Select } from "components"
import moment from "moment"
import { useCoa, useEmployee } from "utils"
import { FormDataContext } from "../../context"
import { useContext, useEffect } from "react"

export function FormSectionProfil(props: { onSubmit: () => void }) {

    const { formState } = useFormContext<ProfilAssetType>()
    const { dataAsset } = useContext(FormDataContext)
    
    return (
        <section className="py-5 px-6">
            <div className="w-full flex-col flex gap-8">
                <LocationDetail />
                <Warranty />
                <LinkAccount />
            </div>
            <div className="mt-10 flex justify-end">
                <Button
                    type="button"
                    color="primary"
                    onClick={() => {
                        props.onSubmit()
                    }}
                    loading={formState.isSubmitting ? "true" : undefined}
                    disabled={Boolean(!dataAsset?.asset_id)}
                >
                    {dataAsset?.asset_profile_id ? "Update" : "Save"}
                </Button>
            </div>
        </section>
    )
}

function LocationDetail() {
    // use form
    const { control, setValue, watch } = useFormContext<ProfilAssetType>()

    // hooks
    const { data: dataDivisi, isLoading: isLoadingDivisi } = useDropdownDivisi()
    const { data: dataDepartment, isLoading: isLoadingDepartment } = useDropdownDepartment({ divisi_id: Number(watch("divisi_id")) })

    const { dataEmployee, isLoadingEmployee } = useEmployee()

    const { dataAsset } = useContext(FormDataContext)

    useEffect(() => {
        if (dataAsset) {
            setValue("divisi_id", dataAsset.divisi_id ?? "")
            setValue("department_id", dataAsset.department_id ?? "")
            setValue("employee_id", dataAsset.employee_id ?? "")
            setValue("warranty_period", dataAsset.warranty_period ?? "")
            setValue("warranty_expired", dataAsset.warranty_expired ?? "")
            setValue("insured", dataAsset.insured ?? "")
            setValue("insurance_policy", dataAsset.insurance_policy ?? "")
            setValue("insurance_expired", dataAsset.insurance_expired ?? "")
            setValue("coa_asset_id", dataAsset.coa_asset_id ?? "")
            setValue("coa_depreciation_id", dataAsset.coa_depreciation_id ?? "")
            setValue("coa_disposal_id", dataAsset.coa_disposal_id ?? "")
            setValue("asset_profile_id", dataAsset.asset_profile_id ?? "")
        }
        // eslint-disable-next-line
    }, [dataAsset, setValue])

    return (
        <div>
            <div className="font-bold mb-4 bg-slate-400 p-2 dark:text-white rounded-sm">LOCATION DETAIL :</div>
            <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                    <Label className="w-[150px]">DIVISION</Label>
                    <Controller
                        control={control}
                        name="divisi_id"
                        render={({ field, fieldState }) => (
                            <Select
                                placeholder="Select division..."
                                {...field}
                                error={fieldState.error?.message}
                                onChange={e => {
                                    field.onChange(e?.value)
                                    setValue("department_id", "")
                                }}
                                options={dataDivisi}
                                isLoading={isLoadingDivisi}
                                value={dataDivisi.find(item => item.value === field.value?.toString())}
                            />
                        )}
                    />
                </div>

                <div className="flex gap-2">
                    <Label className="w-[150px]">DEPARTEMENT</Label>
                    <Controller
                        control={control}
                        name="department_id"
                        render={({ field, fieldState }) => (
                            <Select
                                placeholder="Select department..."
                                {...field}
                                error={fieldState.error?.message}
                                onChange={e => field.onChange(e?.value)}
                                options={dataDepartment}
                                isLoading={isLoadingDepartment}
                                value={dataDepartment.find(item => item.value === field.value?.toString())}
                            />
                        )}
                    />
                </div>

                <div className="flex gap-2">
                    <Label className="w-[150px]">RESPONSIBLE PERSON</Label>
                    <Controller
                        control={control}
                        name="employee_id"
                        render={({ field, fieldState }) => (
                            <Select
                                placeholder="Select responsible person..."
                                {...field}
                                error={fieldState.error?.message}
                                onChange={e => field.onChange(e?.value)}
                                options={dataEmployee}
                                isLoading={isLoadingEmployee}
                                value={dataEmployee.find(item => item.value === field.value?.toString())}
                            />
                        )}
                    />
                </div>
            </div>
        </div>
    )
}

function Warranty() {
    const { control, watch, setValue } = useFormContext<ProfilAssetType>()
    const { dataAsset } = useContext(FormDataContext)

    const insured = [
        { value: "1", label: "Yes" },
        { value: "2", label: "No" },
    ]

    const periodeWaranty = useWatch({
        control,
        name: "warranty_period"
    })


    useEffect(() => {
        setValue("warranty_expired", moment(dataAsset?.acquisition_date).add(periodeWaranty, "years").format("YYYY-MM-DD") )
        // eslint-disable-next-line
    }, [dataAsset, periodeWaranty])

    useEffect(() => {
        if (watch("insured") === "2") {
            setValue("insurance_expired", "")
            setValue("insurance_policy", "")
        }
        // eslint-disable-next-line
    }, [watch("insured")])

    return (
        <div>
            <div className="font-bold mb-4 bg-slate-400 p-2 dark:text-white rounded-sm">WARRANTY & INSURANCE  :</div>
            <div className="flex flex-col gap-2">
                <div className="flex gap-2 w-full">
                    <Label className="flex-shrink-0 min-w-[200px]">WARRANTY PERIODE</Label>
                    <div className="join flex gap-2 justify-between w-full">
                        <div className="w-full">
                            <Controller
                                control={control}
                                name="warranty_period"
                                render={({ field, fieldState }) => (
                                    <NumberInput
                                        {...field}
                                        value={field.value ?? ""}
                                        error={fieldState.error?.message}
                                        onValueChange={(e) => field.onChange(e.value ?? 0)}
                                        inputClass="w-full"
                                        decimalScale={0}
                                    />
                                )}
                            />
                        </div>
                        <div className="flex-shrink-0">
                            <Input
                                inputClass="w-[100px]"
                                disabled
                                value="Year"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex gap-2">
                    <Label className="w-[200px]">WARRANTY EXPIRED DATE</Label>
                    <div className="flex-grow">
                        <Controller
                            control={control}
                            name="warranty_expired"
                            render={({ field, fieldState }) => (
                                <DateInput
                                    onChange={(date) => {
                                        if (date) {
                                            return field.onChange(moment(date).format("YYYY-MM-DD"))
                                        }

                                        field.onChange("")
                                    }}
                                    selected={field.value ? new Date(field.value) : null}
                                    error={fieldState.error?.message}
                                    className="flex-grow"
                                    readOnly
                                />
                            )}
                        />
                    </div>
                </div>

                <div className="flex gap-2">
                    <Label className="flex-grow w-[200px]">INSURED</Label>
                    <Controller
                        control={control}
                        name="insured"
                        render={({ field, fieldState }) => (
                            <Select
                                placeholder="Select Insured..."
                                {...field}
                                error={fieldState.error?.message}
                                onChange={e => field.onChange(e?.value)}
                                options={insured}
                                value={insured.find(item => item.value === field.value?.toString())}
                            />
                        )}
                    />
                </div>

                <div className="flex gap-2">
                    <Label className="flex-grow w-[200px]">INSURANCE POLICY NUMBER </Label>
                    <Controller
                        control={control}
                        name="insurance_policy"
                        render={({ field, fieldState }) => (
                            <Input
                                {...field}
                                value={watch("insured").toString() === "2" ? "" : field.value}
                                error={fieldState.error?.message}
                                className="flex-grow"
                                disabled={watch("insured").toString() === "2"}
                            />
                        )}
                    />
                </div>

                <div className="flex gap-2">
                    <Label className="flex-grow w-[200px]">INSURANCE EXPIRED DATE</Label>
                    <div className="flex-grow">
                        <Controller
                            control={control}
                            name="insurance_expired"
                            render={({ field, fieldState }) => (
                                <DateInput
                                    onChange={(date) => {
                                        if (date) {
                                            return field.onChange(moment(date).format("YYYY-MM-DD"))
                                        }

                                        field.onChange("")
                                    }}
                                    selected={field.value ? moment(field.value).toDate() : null}
                                    error={fieldState.error?.message}
                                    disabled={watch("insured").toString() === "2"}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}

function LinkAccount() {

    // use form
    const { control } = useFormContext<ProfilAssetType>()

    // hooks
    const { data: coa06 } = useCoa({ group_coa_code: "06" });
    const { data: coa09 } = useCoa({ group_coa_code: "09" });
    
    const coaAll = [...(coa06 || []), ...(coa09 || [])];
    const { data: coaAsset } = useCoa({ group_coa_code: "01" });

    return (
        <div>
            <div className="font-bold mb-4 bg-slate-400 p-2 dark:text-white rounded-sm">LINK ACCOUNT  :</div>
            <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                    <Label className="flex-grow w-[200px]">ACCOUNT ASSET</Label>
                    <Controller
                        control={control}
                        name="coa_asset_id"
                        render={({ field, fieldState }) => {
                            return (
                                <Select
                                    placeholder="Select Account Asset..."
                                    {...field}
                                    error={fieldState.error?.message}
                                    onChange={e => field.onChange(e?.value)}
                                    options={coaAsset}
                                    value={coaAsset.find(item => item.value === field.value?.toString())}
                                />
                            );
                        }}
                    />
                </div>

                <div className="flex gap-2">
                    <Label className="flex-grow w-[200px]">ACCOUNT DEPRECIATION</Label>
                    <Controller
                        control={control}
                        name="coa_depreciation_id"
                        render={({ field, fieldState }) => (
                            <Select
                                placeholder="Select Account Depreciation..."
                                {...field}
                                error={fieldState.error?.message}
                                onChange={e => field.onChange(e?.value)}
                                options={coaAll}
                                value={coaAll.find(item => item.value === field.value?.toString())}
                            />
                        )}
                    />
                </div>

                <div className="flex gap-2">
                    <Label className="flex-grow w-[200px]">ACCOUNT DISPOSAL</Label>
                    <Controller
                        control={control}
                        name="coa_disposal_id"
                        render={({ field, fieldState }) => (
                            <Select
                                placeholder="Select Account Disposal..."
                                {...field}
                                error={fieldState.error?.message}
                                onChange={e => field.onChange(e?.value)}
                                options={coaAll}
                                value={coaAll.find(item => item.value === field.value?.toString())}
                            />
                        )}
                    />
                </div>


            </div>
        </div>
    )
}